import { useState, useEffect, useRef } from "react";
import NotificationImage from "../assets/notificationImage.png";
import SubNotificationImage from "../assets/subNotificationImage.png";
import { NotificationServices } from "../services/NotificationServices";
import {
  fetchNotificationCount,
  fetchCommentCount,
} from "../redux/notificationSlice";
import { CircularProgress } from "@mui/material";
import KeyboardDoubleArrowRightOutlinedIcon from "@mui/icons-material/KeyboardDoubleArrowRightOutlined";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { handleApiErrorResponses } from "../utils/ApisErrorHandling";

const NotificationPopUp = ({ setOpen, parentRef, notificationCategory }) => {
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [notificationData, setNotificationData] = useState([]);
  const [readStatus, setReadStatus] = useState("all");
  const [isLoading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const dispatch = useDispatch();
  const ref = useRef();
  const listRef = useRef();
  const navigate = useNavigate();

  const getTime = (createdDate) => {
    const currentDate = new Date();
    const diffInMs = currentDate - new Date(createdDate);

    const days = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (diffInMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffInMs % (1000 * 60)) / 1000);

    return `${days > 0 ? days + "D " : ""}${hours > 0 ? hours + "H " : ""}${
      minutes > 0 ? minutes + "M " : ""
    }${seconds}S`;
  };

  const getDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const markNotificationStatus = async (notificationId, status) => {
    try {
      await NotificationServices.markNotificationReadUnread(
        [notificationId],
        status
      );
      dispatch(fetchNotificationCount());
    } catch (error) {
      handleApiErrorResponses(error);
    }
  };

  const markedAllReadUnread = async () => {
    try {
      await NotificationServices.markAllNotificationsReadUnread(true);
      dispatch(fetchNotificationCount());
      dispatch(fetchCommentCount());
      setOpen(false);
      window.alert("Notifications Updated Successfully");
    } catch (error) {
      handleApiErrorResponses(error);
    }
  };

  const getUserNotification = async (reset = false) => {
    setLoading(true);
    try {
      const response = await NotificationServices.getNotifications(
        readStatus,
        "all",
        10,
        reset ? 1 : page,
        notificationCategory
      );
      const newNotifications = response?.data?.UserNotifcations || [];

      setNotificationData((prevData) =>
        reset ? newNotifications : [...(prevData || []), ...newNotifications]
      );

      if (newNotifications.length < 10) setHasMore(false);
      else setHasMore(true);
    } catch (error) {
      handleApiErrorResponses(error);
    }
    setLoading(false);
  };

  // Initial load and close on outside click
  useEffect(() => {
    const checkIfClickedOutside = (e) => {
      if (parentRef.current && parentRef.current.contains(e.target)) {
        setOpen(true);
      } else if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("click", checkIfClickedOutside);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("click", checkIfClickedOutside);
      document.body.style.overflow = "auto";
    };
  }, []);

  // Reset data on readStatus change
  useEffect(() => {
    setPage(1);
    readStatus !== "mark all read" && getUserNotification(true);
  }, [readStatus]);

  // Infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      const element = listRef.current;
      if (!element || isLoading || !hasMore) return;

      const { scrollTop, scrollHeight, clientHeight } = element;
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        setPage((prev) => prev + 1);
      }
    };

    const currentRef = listRef.current;
    currentRef?.addEventListener("scroll", handleScroll);
    return () => currentRef?.removeEventListener("scroll", handleScroll);
  }, [isLoading, hasMore]);

  // Fetch next page
  useEffect(() => {
    if (page > 1) getUserNotification(false);
  }, [page]);

  return (
    <div
      style={{ border: "1px solid #E4E4E4" }}
      className="absolute top-[220%] -left-[95px] w-[400px] rounded-[8px] bg-white p-2 z-20"
    >
      <div className="flex justify-end gap-2 text-xs text-white mb-2">
        <div
          style={{ border: "1px solid #017C57" }}
          className={`${
            readStatus === "mark all read"
              ? "text-white bg-[#017C57]"
              : "text-[#017C57] bg-white"
          } px-5 py-2 rounded-full`}
          onClick={() => {
            setReadStatus("mark all read");
            markedAllReadUnread();
            dispatch(fetchNotificationCount());
          }}
        >
          Mark All READ
        </div>
        <div
          style={{ border: "1px solid #017C57" }}
          className={`${
            readStatus === true
              ? "text-white bg-[#017C57]"
              : "text-[#017C57] bg-white"
          } px-5 py-2 rounded-full`}
          onClick={() => setReadStatus(true)}
        >
          READ
        </div>
        <div
          style={{ border: "1px solid #017C57" }}
          className={`${
            readStatus === false
              ? "text-white bg-[#017C57]"
              : "text-[#017C57] bg-white"
          } px-5 py-2 rounded-full`}
          onClick={() => setReadStatus(false)}
        >
          UNREAD
        </div>
        <div
          style={{ border: "1px solid #017C57" }}
          className={`${
            readStatus === "all"
              ? "text-white bg-[#017C57]"
              : "text-[#017C57] bg-white"
          } px-5 py-2 rounded-full`}
          onClick={() => setReadStatus("all")}
        >
          ALL
        </div>
      </div>

      <div ref={ref}>
        <div ref={listRef} className="max-h-[400px] overflow-y-scroll pr-1">
          {notificationData && notificationData.length > 0 ? (
            notificationData.map((notification, idx) => (
              <div key={idx}>
                <div
                  className={`flex items-start gap-[10px] p-2 mb-1 rounded-[12px] ${
                    notification?.read ? "bg-white" : "bg-neutral-100"
                  }`}
                >
                  <img
                    src={notification?.notificationIcon || NotificationImage}
                    alt="notification-image"
                    height={32}
                    width={36}
                  />
                  <div
                    className="w-full"
                    onClick={() => {
                      setSelectedNotification(idx);
                      if (!notification.read) {
                        markNotificationStatus(notification?.id, "true");
                      }
                    }}
                  >
                    <div
                      className={`flex justify-between gap-1 w-full text-sm ${
                        notification?.read ? "font-medium" : "font-semibold"
                      } tracking-tight`}
                      style={{ color: "#191919" }}
                    >
                      {notification?.title}
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpen(false);
                          if (
                            notification?.title
                              ?.toLowerCase()
                              .includes("dm otp")
                          ) {
                            navigate(`/dashboard/digital-medco-otp`);
                          } else {
                            navigate(
                              `/Dashboard/EditCases/?caseId=${notification.caseId}`,
                              {
                                state: { caseId: notification.caseId },
                              }
                            );
                          }
                        }}
                      >
                        <KeyboardDoubleArrowRightOutlinedIcon />
                      </div>
                    </div>
                    <div className="text-sm text-left text-[#797979] mt-1 tracking-tight ">
                      {getTime(notification?.createdAt)} -
                      {getDate(notification?.createdAt)}
                    </div>
                  </div>
                </div>

                {selectedNotification === idx && (
                  <div className="flex gap-[10px]">
                    <div className="flex items-start gap-4 p-2">
                      <img src={SubNotificationImage} alt="sub-image" />
                      <div className="text-start">
                        <div className="text-sm text-[#797979] mt-1 tracking-tight">
                          {notification?.body}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-center mt-3 font-semibold">
              No Notifications Present
            </div>
          )}

          {isLoading && (
            <div className="flex justify-center py-3">
              <CircularProgress size={20} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationPopUp;
